import { observer } from "mobx-react-lite";
import { GitMergeIcon } from "lucide-react";
import { AdjustmentsIcon } from "@heroicons/react/outline";

import { BlockProps } from "@/components/PowerBlock/Block";
import { withBlockStore, useBlock } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { DoubleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { ApplyDiffConfiguration } from "@/types/evaluate";
import VerboseModeToggle from "@/components/PowerBlock/Blocks/categories/data-sources/PromptTemplateBlock/VerboseModeToggle";

const validationSchema = createBlockSchema<ApplyDiffConfiguration>()
  .requireName()
  .requireDoubleSource()
  .build();

const ApplyDiffBlockCore = observer(
  ({
    sources: availableSources = createEmptySources(),
  }: Omit<BlockProps<ApplyDiffConfiguration>, "DefaultFields">) => {
    const isReadonly = useIsReadonly();
    const blockStore = useBlock<ApplyDiffConfiguration>();

    return (
      <>
        <SectionGroup
          title="Data Sources"
          description="Select the original content and the diff patch to apply"
          icon={<GitMergeIcon className="h-4 w-4" />}
        >
          <DoubleSourceSelector
            sources={availableSources}
            firstName="Original Content"
            secondName="Diff Patch"
            firstValidationPath="configuration.sources.0"
            secondValidationPath="configuration.sources.1"
            disabled={isReadonly}
          />
        </SectionGroup>

        <SectionGroup
          title="Advanced Settings"
          variant="compact"
          collapsible
          defaultExpanded={false}
          icon={<AdjustmentsIcon className="h-4 w-4" />}
        >
          <VerboseModeToggle
            enabled={blockStore.configuration?.verbose || false}
            onChange={enabled =>
              blockStore.setConfiguration({
                ...(blockStore.configuration || {}),
                verbose: enabled,
              } as ApplyDiffConfiguration)
            }
            tooltipContent="Show full payload: original content, diff, patched content, and any error message."
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<ApplyDiffConfiguration>(ApplyDiffBlockCore, {
  validationSchema,
});
